import { useState } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  LayoutGrid,
  List as ListIcon,
  ChevronRight,
  ChevronDown,
  X,
} from "lucide-react";
import {
  useProjectTasks,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useSubtask,
  useMembers,
  type ProjectTask,
  type TareaEstado,
  type Prioridad,
  type UserStory,
} from "../api/projects";

const COLUMNS: { key: TareaEstado; label: string }[] = [
  { key: "pendiente", label: "Pendiente" },
  { key: "progreso", label: "En progreso" },
  { key: "revision", label: "Revisión" },
  { key: "completado", label: "Completado" },
];

const PRIO: Record<Prioridad, string> = {
  alta: "bg-rose-100 text-rose-700",
  media: "bg-amber-100 text-amber-700",
  baja: "bg-slate-100 text-slate-500",
};

export function TareasTab({ projectId, stories }: { projectId: number; stories: UserStory[] }) {
  const { data: tasks, isLoading } = useProjectTasks(projectId);
  const [view, setView] = useState<"kanban" | "lista">("kanban");
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
          <button onClick={() => setView("kanban")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold ${view === "kanban" ? "bg-brand-700 text-white" : "bg-white text-slate-600"}`}><LayoutGrid size={15} /> Kanban</button>
          <button onClick={() => setView("lista")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold ${view === "lista" ? "bg-brand-700 text-white" : "bg-white text-slate-600"}`}><ListIcon size={15} /> Lista</button>
        </div>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-3 py-2 text-sm font-bold"><Plus size={15} /> Nueva tarea</button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando tareas…</div>
      ) : view === "kanban" ? (
        <KanbanView projectId={projectId} tasks={tasks ?? []} />
      ) : (
        <ListView projectId={projectId} tasks={tasks ?? []} />
      )}

      {showCreate && <CreateTaskModal projectId={projectId} stories={stories} onClose={() => setShowCreate(false)} />}
    </div>
  );
}

/* ── Kanban ────────────────────────────────────────────── */

function KanbanView({ projectId, tasks }: { projectId: number; tasks: ProjectTask[] }) {
  const update = useUpdateTask(projectId);
  const del = useDeleteTask(projectId);
  const [dragId, setDragId] = useState<number | null>(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      {COLUMNS.map((col) => {
        const colTasks = tasks.filter((t) => t.estado === col.key);
        return (
          <div
            key={col.key}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (dragId != null) {
                const t = tasks.find((x) => x.id === dragId);
                if (t && t.estado !== col.key) update.mutate({ id: dragId, payload: { estado: col.key } });
                setDragId(null);
              }
            }}
            className="rounded-2xl bg-slate-50 border border-slate-200/70 p-3 min-h-[120px]"
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-xs font-black uppercase tracking-wider text-slate-500">{col.label}</span>
              <span className="text-xs font-bold text-slate-400">{colTasks.length}</span>
            </div>
            <div className="flex flex-col gap-2">
              {colTasks.map((t) => (
                <div
                  key={t.id}
                  draggable
                  onDragStart={() => setDragId(t.id)}
                  className="group rounded-xl bg-white border border-slate-200 p-3 shadow-sm cursor-grab active:cursor-grabbing"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-slate-800 text-sm">{t.titulo}</span>
                    <button onClick={() => { if (confirm("¿Eliminar tarea?")) del.mutate(t.id); }} className="opacity-0 group-hover:opacity-100 text-rose-400 hover:text-rose-600"><Trash2 size={14} /></button>
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${PRIO[t.prioridad]}`}>{t.prioridad}</span>
                    {t.subtareas.length > 0 && <span className="text-[11px] text-slate-400">{t.subtareas.filter((s) => s.completada).length}/{t.subtareas.length} subtareas</span>}
                    {t.asignado && <span className="ml-auto text-[11px] font-semibold text-slate-500 truncate max-w-[90px]">{t.asignado.nombre}</span>}
                  </div>
                </div>
              ))}
              {colTasks.length === 0 && <p className="text-xs text-slate-300 px-1">—</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Lista ─────────────────────────────────────────────── */

function ListView({ projectId, tasks }: { projectId: number; tasks: ProjectTask[] }) {
  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white divide-y divide-slate-100 max-w-3xl">
      {tasks.length === 0 && <p className="text-sm text-slate-400 p-4">Sin tareas.</p>}
      {tasks.map((t) => <TaskRow key={t.id} projectId={projectId} task={t} />)}
    </div>
  );
}

function TaskRow({ projectId, task }: { projectId: number; task: ProjectTask }) {
  const update = useUpdateTask(projectId);
  const del = useDeleteTask(projectId);
  const sub = useSubtask(projectId);
  const [open, setOpen] = useState(false);
  const [newSub, setNewSub] = useState("");

  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-2">
        <button onClick={() => setOpen((o) => !o)} className="text-slate-400 hover:text-slate-700">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <span className="font-semibold text-slate-800 flex-1 truncate">{task.titulo}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${PRIO[task.prioridad]}`}>{task.prioridad}</span>
        <select
          className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold"
          value={task.estado}
          onChange={(e) => update.mutate({ id: task.id, payload: { estado: e.target.value as TareaEstado } })}
        >
          {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <button onClick={() => { if (confirm("¿Eliminar tarea?")) del.mutate(task.id); }} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 size={15} /></button>
      </div>

      {open && (
        <div className="mt-2 ml-6 flex flex-col gap-1.5">
          {task.subtareas.map((s) => (
            <div key={s.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={s.completada} onChange={() => sub.toggle.mutate(s.id)} />
              <span className={s.completada ? "line-through text-slate-400" : "text-slate-700"}>{s.titulo}</span>
              <button onClick={() => sub.remove.mutate(s.id)} className="ml-auto text-rose-300 hover:text-rose-600"><X size={13} /></button>
            </div>
          ))}
          <form
            onSubmit={(e) => { e.preventDefault(); if (newSub.trim()) { sub.create.mutate({ taskId: task.id, titulo: newSub.trim() }); setNewSub(""); } }}
            className="flex gap-2 mt-1"
          >
            <input className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-sm" placeholder="Añadir subtarea…" value={newSub} onChange={(e) => setNewSub(e.target.value)} />
            <button type="submit" className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-bold text-slate-600 hover:bg-slate-50"><Plus size={13} /></button>
          </form>
        </div>
      )}
    </div>
  );
}

/* ── Crear tarea ───────────────────────────────────────── */

function CreateTaskModal({ projectId, stories, onClose }: { projectId: number; stories: UserStory[]; onClose: () => void }) {
  const create = useCreateTask(projectId);
  const { data: members } = useMembers();
  const [form, setForm] = useState({
    titulo: "",
    prioridad: "media" as Prioridad,
    estado: "pendiente" as TareaEstado,
    asignado_id: "" as string,
    user_story_id: "" as string,
  });
  const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.titulo.trim()) return;
    await create.mutateAsync({
      titulo: form.titulo.trim(),
      prioridad: form.prioridad,
      estado: form.estado,
      asignado_id: form.asignado_id ? Number(form.asignado_id) : null,
      user_story_id: form.user_story_id ? Number(form.user_story_id) : null,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-black text-slate-900">Nueva tarea</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <input className={input} placeholder="Título de la tarea" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} autoFocus />
        <div className="grid grid-cols-2 gap-3">
          <select className={input} value={form.prioridad} onChange={(e) => setForm({ ...form, prioridad: e.target.value as Prioridad })}>
            <option value="alta">Prioridad alta</option>
            <option value="media">Prioridad media</option>
            <option value="baja">Prioridad baja</option>
          </select>
          <select className={input} value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value as TareaEstado })}>
            {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <select className={input} value={form.asignado_id} onChange={(e) => setForm({ ...form, asignado_id: e.target.value })}>
          <option value="">Sin responsable</option>
          {members?.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
        </select>
        {stories.length > 0 && (
          <select className={input} value={form.user_story_id} onChange={(e) => setForm({ ...form, user_story_id: e.target.value })}>
            <option value="">Sin historia de usuario</option>
            {stories.map((s) => <option key={s.id} value={s.id}>{s.titulo}</option>)}
          </select>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
          <button type="submit" disabled={create.isPending || !form.titulo.trim()} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
            {create.isPending ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Crear
          </button>
        </div>
      </form>
    </div>
  );
}
